import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
    Container,
    Typography,
    Card,
    CardContent,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Box,
    Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import axiosInstance from '../axiosConfig';
import Layout from './Layout';
import TemplateEdit from './TemplateEdit';
import { colors } from '../colors';
import { useNavigate } from 'react-router-dom';


const TemplateView = () => {
    const { templateId } = useParams();
    const [template, setTemplate] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const navigate = useNavigate();


    useEffect(() => {
        const fetchTemplate = async () => {
            try {
                const response = await axiosInstance.get(`/api/v2/templates/${templateId}`);
                setTemplate(response.data);
            } catch (error) {
                console.error('Error fetching template:', error);
            }
        };
        fetchTemplate();
    }, [templateId]);

    const handleEditClick = () => {
        setIsEditing(true);
    };

    const handleBackClick = () => {
        navigate('/admin/dashboard', { state: { view: 'TemplateManagement' } });
    };
    

    const handleSaveChanges = async (updatedTemplate) => {
        try {
            await axiosInstance.put(`/api/admin/templates/${templateId}`, updatedTemplate);
            setTemplate(updatedTemplate);
            setIsEditing(false);
        } catch (error) {
            console.error('Error saving template:', error);
        }
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
    };

    const getConditionDisplayText = (condition) => {
        if (!condition) return 'No conditions';
        return `Depends on question ${condition.questionId} with answer "${condition.expectedAnswer}"`;
    };

    return (
        <Layout>
            {/* Blue Banner */}
            <Box
                sx={{
                    backgroundColor: colors.primaryBannerBlue,
                    color: 'white',
                    padding: { xs: '5px 0', sm: '10px 0' }, // Responsive padding
                    textAlign: 'center',
                    fontSize: { xs: '1rem', sm: '1.2rem' },
                    fontWeight: '500',
                }}
            >
                Templates
            </Box>

            {/* Content Area with Background Color */}
            <Box
                sx={{
                    backgroundColor: '#eaecee', // Set the desired background color
                    minHeight: '100vh', // Ensures the content area covers the full height
                    py: 3, // Add vertical padding for spacing
                    px: { xs: 2, sm: 3, md: 3 }
                }}
            >
                {!isEditing ? (
                    <Box
                        sx={{
                            width: '90%', // Adjust box width
                            margin: '0 auto', // Center horizontally
                            minHeight: '80vh', // Adjust height to look balanced
                            backgroundColor: '#ffffff', // Light background for the box
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)', // Subtle shadow for depth
                            border: '1px solid #d3d3d3', // Light gray border
                            borderRadius: '12px', // Rounded corners
                            padding: 3, // Padding inside the box
                            boxSizing: 'border-box',
                        }}
                    >
                        {/* Template Header */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                            <Button
                                variant="contained"
                                startIcon={<ArrowBackIcon />}
                                onClick={handleBackClick}
                                sx={{
                                    backgroundColor: colors.primaryBannerBlue,
                                    color: 'white',
                                    '&:hover': { backgroundColor: colors.primaryBannerBlueHover },
                                }}
                            >
                                BACK
                            </Button>
                            <Typography variant="h4">{template?.title}</Typography>
                            <Button
                                variant="contained"
                                startIcon={<EditIcon />}
                                onClick={handleEditClick}
                                sx={{
                                    backgroundColor: colors.primaryBannerBlue,
                                    color: 'white',
                                    '&:hover': { backgroundColor: colors.primaryBannerBlueHover },
                                }}
                            >
                                Edit
                            </Button>
                        </Box>

                        <Typography variant="subtitle1" color="textSecondary" gutterBottom>
                            Template ID: {template?.id}
                        </Typography>
                        <Divider sx={{ my: 3 }} />

                        {/* Sections */}
                        <Typography variant="h5" gutterBottom>
                            Sections
                        </Typography>
                        {template?.sections.map((section, sectionIndex) => (
                            <Accordion key={sectionIndex}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography variant="h6">
                                        {section.sectionTitle} ({section.questions.length} questions)
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    {/* Questions Table */}
                                    <TableContainer>
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>#</TableCell>
                                                    <TableCell>Question</TableCell>
                                                    <TableCell>Type</TableCell>
                                                    <TableCell>Condition</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {section.questions.map((question, questionIndex) => (
                                                    <TableRow key={questionIndex}>
                                                        <TableCell>{question.questionId || questionIndex + 1}</TableCell>
                                                        <TableCell>{question.questionText}</TableCell>
                                                        <TableCell>{question.type}</TableCell>
                                                        <TableCell>
                                                            {getConditionDisplayText(question.condition)}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Box>
                ) : (
                    <TemplateEdit
                        template={template}
                        onCancel={handleCancelEdit} // Pass handleCancelEdit to TemplateEdit
                    />
                )}

            </Box>
        </Layout>
    );
};

export default TemplateView;
